import { z } from 'zod'
import { validarCUIT } from '@/lib/utils'
import type { PersonaAFIP } from '@/types/afip'

/** Condiciones frente al IVA que acepta la function (las mapea a los códigos de RG 5616). */
export const CONDICIONES_IVA_RECEPTOR = [
  { value: 'consumidor_final', label: 'Consumidor final' },
  { value: 'monotributista', label: 'Monotributista' },
  { value: 'responsable_inscripto', label: 'Responsable inscripto' },
  { value: 'exento', label: 'Exento' },
  { value: 'no_categorizado', label: 'Sujeto no categorizado' },
  { value: 'no_alcanzado', label: 'IVA no alcanzado' },
] as const

export type CondicionIvaReceptor = (typeof CONDICIONES_IVA_RECEPTOR)[number]['value']

/** Consumidor final sin identificar admitido hasta este total (RG 5700/2025). El servidor exige lo mismo. */
export const TOPE_CONSUMIDOR_FINAL_SIN_IDENTIFICAR = 10_000_000

const IMPORTE = /^\d{1,12}([.,]\d{1,2})?$/
const PERIODO = /^\d{4}-(0[1-9]|1[0-2])$/

/** «1.500,50», «1500.5» o «1500» → número. NaN si no es un importe válido. */
export const parsearImporte = (s: string): number => {
  const limpio = s.trim().replace(/\.(?=\d{3}(\D|$))/g, '')
  return IMPORTE.test(limpio) ? Number(limpio.replace(',', '.')) : NaN
}

const lineaSchema = z.object({
  descripcion: z.string().trim().min(1, 'Falta la descripción').max(200, 'Hasta 200 caracteres'),
  importe: z.string().refine((s) => parsearImporte(s) > 0, 'Importe inválido'),
  periodo: z.string().refine((s) => s === '' || PERIODO.test(s), 'Período AAAA-MM'),
})

/** Con el CUIT del emisor, además rechaza facturarse a sí mismo (ORR-667). */
export const crearFacturaManualSchema = (cuitEmisor?: string | null) => z
  .object({
    tipoDoc: z.enum(['cuit', 'dni', 'ninguno']),
    docNro: z.string(),
    nombre: z.string().trim().min(1, 'Falta el nombre o la razón social').max(200, 'Hasta 200 caracteres'),
    condicionIva: z.enum(CONDICIONES_IVA_RECEPTOR.map((c) => c.value) as [CondicionIvaReceptor, ...CondicionIvaReceptor[]]),
    domicilio: z.string().max(300, 'Hasta 300 caracteres'),
    email: z.string().trim().refine((s) => s === '' || z.email().safeParse(s).success, 'Email inválido'),
    enviarEmail: z.boolean(),
    lineas: z.array(lineaSchema).min(1, 'Agregá al menos una línea'),
  })
  .superRefine((v, ctx) => {
    const digitos = v.docNro.replace(/\D/g, '')
    if (v.tipoDoc === 'cuit' && !validarCUIT(digitos).valido) {
      ctx.addIssue({ code: 'custom', path: ['docNro'], message: 'CUIT inválido' })
    }
    if (v.tipoDoc === 'cuit' && cuitEmisor && digitos === cuitEmisor.replace(/\D/g, '')) {
      ctx.addIssue({ code: 'custom', path: ['docNro'], message: 'Es el CUIT del emisor: no se puede facturar a sí mismo' })
    }
    if (v.tipoDoc === 'dni' && !/^\d{7,8}$/.test(digitos)) {
      ctx.addIssue({ code: 'custom', path: ['docNro'], message: 'El DNI tiene 7 u 8 dígitos' })
    }
    const total = v.lineas.reduce((s, l) => s + (parsearImporte(l.importe) || 0), 0)
    const sinIdentificar = v.tipoDoc === 'ninguno'
    if (sinIdentificar && v.condicionIva !== 'consumidor_final') {
      ctx.addIssue({ code: 'custom', path: ['tipoDoc'], message: 'Sin documento solo se factura a consumidor final' })
    }
    if (sinIdentificar && total >= TOPE_CONSUMIDOR_FINAL_SIN_IDENTIFICAR) {
      ctx.addIssue({ code: 'custom', path: ['tipoDoc'], message: 'Por el monto, ARCA exige CUIT o DNI' })
    }
    if (!(sinIdentificar && total < TOPE_CONSUMIDOR_FINAL_SIN_IDENTIFICAR) && !v.domicilio.trim()) {
      ctx.addIssue({ code: 'custom', path: ['domicilio'], message: 'ARCA exige el domicilio del receptor' })
    }
    if (v.enviarEmail && !v.email) {
      ctx.addIssue({ code: 'custom', path: ['email'], message: 'Falta el email para enviarla' })
    }
  })

export const facturaManualSchema = crearFacturaManualSchema()

export type FacturaManualFormValues = z.infer<typeof facturaManualSchema>

export const LINEA_VACIA: FacturaManualFormValues['lineas'][number] = { descripcion: '', importe: '', periodo: '' }

export const DEFAULT_FACTURA_MANUAL: FacturaManualFormValues = {
  tipoDoc: 'cuit',
  docNro: '',
  nombre: '',
  condicionIva: 'consumidor_final',
  domicilio: '',
  email: '',
  enviarEmail: true,
  lineas: [{ ...LINEA_VACIA }],
}

/** Lo que recibe la function: el documento según el tipo y los importes como número. */
export const aPedidoManual = (v: FacturaManualFormValues) => {
  const digitos = v.docNro.replace(/\D/g, '')
  return {
    receptor: {
      nombre: v.nombre.trim(),
      cuit: v.tipoDoc === 'cuit' ? digitos : null,
      dni: v.tipoDoc === 'dni' ? digitos : null,
      condicionIva: v.condicionIva,
      domicilio: v.domicilio.trim() || null,
      email: v.email.trim() || null,
    },
    lineas: v.lineas.map((l) => ({
      descripcion: l.descripcion.trim(),
      importe: parsearImporte(l.importe),
      periodo: l.periodo || null,
    })),
    enviarEmail: v.enviarEmail && !!v.email.trim(),
  }
}

/** Domicilio fiscal del padrón en una línea, como lo imprime el comprobante. */
export const domicilioDelPadron = (p: Pick<PersonaAFIP, 'domicilio'>): string =>
  [p.domicilio.direccion, p.domicilio.localidad, p.domicilio.provincia]
    .map((x) => x?.trim())
    .filter(Boolean)
    .join(', ')

export const nombreDelPadron = (p: Pick<PersonaAFIP, 'nombre' | 'apellido' | 'razonSocial'>): string =>
  p.razonSocial?.trim() || [p.apellido, p.nombre].filter(Boolean).join(' ').trim()
